import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import useContracts from 'modules/contracts/hooks/useContracts'
import { useHasToRequestPromotion } from 'modules/contracts/hooks/useHasToRequestPromotion'
import type Contract from 'modules/contracts/lib/Contract'
import { hasOngoing, hasOngoingOrUpcomingContract } from 'modules/contracts/lib/contractSchedule'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import ExpiredOrUpcomingModalContent from './Modals/ExpiredOrUpcomingModalContent'
import OngoingModalContent from './Modals/OngoingModalContent'
import { FLOWS } from './constants'

const sortContractVersions = (allContractVersions: ContractVersion[]) => {
  return sortBy(allContractVersions, ['effectiveOn'])
}

const getTitle = (contracts: Contract[], employeeFullName: string) => {
  if (hasOngoing(contracts))
    return i18n.t('contract_versions.modals.quick_flows.title.ongoing_with_name', {
      employeeFullName: employeeFullName,
    })

  return i18n.t('contract_versions.modals.quick_flows.title.upcoming_with_name', {
    employeeFullName: employeeFullName,
  })
}

interface Props {
  onCloseModal: () => void
  selectedContractVersion: ContractVersion
  employee: Employee
  allContractVersions: ContractVersions
  setOption: React.Dispatch<React.SetStateAction<string>>
  option: string
  showFixOption?: boolean
}

const FlowSelectorModal = ({
  onCloseModal,
  selectedContractVersion,
  employee,
  allContractVersions,
  setOption,
  option,
  showFixOption,
}: Props) => {
  const { contracts } = useContracts(
    selectedContractVersion,
    sortContractVersions(allContractVersions.models)
  )

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { loading } = useAsyncData(async () => {
    await Promise.all([policyAuthorizations.authorizePolicies('contracts.promote', [employee])])
  }, [employee.id])
  const { hasToRequestPromotion } = useHasToRequestPromotion()

  const canPromote = policyAuthorizations.for('contracts.promote', employee)

  const title = getTitle(contracts, employeeUser(employee).fullName())

  const onChange = (option: React.SetStateAction<string>) => {
    setOption(option)
  }

  const getContractStatus = () => {
    if (hasOngoing(contracts)) return 'ongoing'
    if (!hasOngoingOrUpcomingContract(contracts)) return 'expired'
    return 'upcoming'
  }

  const withoutContracts = allContractVersions.length === 0

  const onPromote = () => {
    track(trackingEvents.employee.contracts.newContractConditionsStart, {
      contract_status: getContractStatus(),
      update_type: FLOWS.promotion,
      approval: hasToRequestPromotion,
    })
    navigate(
      navigation.employees.id.contractVersions.promotion.fullPath({
        id: employee.id.toString(),
      })
    )
  }

  const onFix = () => {
    track(trackingEvents.employee.contracts.fixContractStart, {
      contract_status: getContractStatus(),
    })
    navigate(
      navigation.employees.id.contractVersions.fix.fullPath({
        id: employee.id.toString(),
        contractVersionId: selectedContractVersion.id,
      })
    )
  }

  const onEvolve = () => {
    track(trackingEvents.employee.contracts.newContractConditionsStart, {
      contract_status: getContractStatus(),
      update_type: FLOWS.evolution,
    })
    navigate(
      navigation.employees.id.contractVersions.update.fullPath({
        id: employee.id.toString(),
      })
    )
  }

  const onSubmit = () => {
    switch (option) {
      case FLOWS.promotion:
        onPromote()
        break
      case FLOWS.evolution:
      case FLOWS.creation:
        onEvolve()
        break
      case FLOWS.fix:
        onFix()
        break
      default:
        throw new Error(`Case ${option} not found.`)
    }
  }
  let modalContent = undefined
  if (hasOngoing(contracts) && canPromote) {
    modalContent = (
      <OngoingModalContent
        showFixOption={showFixOption ?? true}
        employee={employee}
        value={option}
        onChange={onChange}
      />
    )
  } else {
    modalContent = (
      <ExpiredOrUpcomingModalContent
        showFixOption={showFixOption ?? true}
        employeeFullname={employeeUser(employee).fullName()}
        value={option}
        onChange={onChange}
        withoutContracts={withoutContracts}
      />
    )
  }

  const modalDescription = (
    <>
      {modalContent}
      <Box>
        <Text.Normal weight="normal" size="200" color="grey800">
          {i18n.t('contract_versions.modals.quick_flows.footer')}
        </Text.Normal>
      </Box>
    </>
  )

  return (
    <Modal
      title={title}
      illustration="table-file"
      onSubmit={onSubmit}
      onClose={onCloseModal}
      description={modalDescription}
      footer={
        <>
          <Button disabled={false} onClick={onCloseModal} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.next')}</Button>
        </>
      }
    >
      {loading && <Spinner />}
    </Modal>
  )
}

export default observer(FlowSelectorModal)
